import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ContactSection from "@/components/ContactSection";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { useHead } from "@/lib/head";
import { ArrowLeft, Flame, Mail, MapPin, MessageCircle, Package, Phone, Sprout, Truck, Wheat } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import bannerImage from "@/assets/store-delivery-truck.jpg";

// I comuni elencati qui sono gli stessi dichiarati nell'areaServed dello schema
// in index.html: se cambia il raggio vanno aggiornati tutti e due, altrimenti
// la pagina e i dati strutturati promettono zone diverse.
const ZONE = [
  {
    area: "Lago Trasimeno",
    comuni: ["Magione", "Passignano sul Trasimeno", "Tuoro sul Trasimeno", "Castiglione del Lago", "Panicale"],
  },
  {
    area: "Perugia e dintorni",
    comuni: ["Perugia", "Corciano", "Torgiano"],
  },
  {
    area: "Media valle del Tevere",
    comuni: ["Deruta", "Umbertide"],
  },
];

const COSA_CONSEGNAMO = [
  {
    icon: Flame,
    titolo: "Pellet e legna da ardere",
    testo:
      "Pellet certificato ENplus in sacchi da 15 kg e bancali, legna da ardere. È qui che il trasporto conviene di più: sono i prodotti più pesanti e ingombranti da caricare in auto.",
    link: "/prodotti/pellet-combustibili",
  },
  {
    icon: Wheat,
    titolo: "Mangimi per allevamento",
    testo:
      "Mangimi zootecnici per bovini, suini, ovini, pollame e conigli, in sacchi o bancali interi. Consegna anche periodica per chi ha una stalla da rifornire.",
    link: "/prodotti/mangimi-allevamento",
  },
  {
    icon: Package,
    titolo: "Terricci e concimi",
    testo:
      "Terricci, torba e concimi, fino ai big bag e ai balloni per uso professionale. Volumi che difficilmente si trasportano da soli.",
    link: "/prodotti/concimi-terricci",
  },
  {
    icon: Sprout,
    titolo: "Sementi e piantine",
    testo:
      "Sementi per orto e prato, piantine stagionali e bulbi, insieme al resto dell'ordine quando serve rifornire l'orto o un tappeto erboso.",
    link: "/prodotti/sementi-piantine",
  },
];

const Consegne = () => {
  useHead("servizio-consegne", [
    {
      tag: "ldjson",
      data: {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": "https://rubinimagione.it/consegne#servizio",
        name: "Consegna a domicilio di pellet, legna, mangimi e terricci",
        serviceType: "Consegna a domicilio",
        description:
          "Consegna a domicilio di pellet, legna da ardere, mangimi zootecnici, terricci e concimi entro 30 km dalla sede di Magione (PG).",
        provider: { "@id": "https://rubinimagione.it/#negozio" },
        areaServed: [
          {
            "@type": "GeoCircle",
            description: "Consegna a domicilio entro 30 km dalla sede di Magione",
            geoMidpoint: { "@type": "GeoCoordinates", latitude: 43.1389, longitude: 12.2069 },
            geoRadius: "30000",
          },
          ...ZONE.flatMap((z) => z.comuni).map((name) => ({ "@type": "City", name })),
        ],
        availableChannel: {
          "@type": "ServiceChannel",
          serviceUrl: "https://rubinimagione.it/consegne",
          servicePhone: { "@type": "ContactPoint", telephone: "+39 346 625 7639" },
        },
      },
    },
  ]);

  return (
    <div className="min-h-screen">
      <SEO
        title="Consegna a Domicilio Pellet, Mangimi e Legna a Magione"
        description="Consegniamo pellet, legna, mangimi e terricci entro 30 km da Magione: Perugia, Corciano, Castiglione del Lago e tutto il lago Trasimeno."
        canonical="/consegne"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Consegne a domicilio", url: "/consegne" },
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative h-[450px] md:h-[500px] overflow-hidden pt-24 md:pt-28">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${bannerImage})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />

        <div className="relative container mx-auto px-4 h-full flex flex-col justify-start md:justify-center pt-2 md:pt-0">
          <Link to="/" className="mb-6 w-fit">
            <Button variant="outline" className="bg-white/10 text-white border-white/30 hover:bg-white/20 backdrop-blur-sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna alla home
            </Button>
          </Link>

          <div className="max-w-4xl">
            <div className="icon-wrapper mb-6 w-16 h-16 text-white hidden md:flex items-center justify-center">
              <Truck className="w-12 h-12" />
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6 text-white">
              Consegna a domicilio entro 30 km da Magione
            </h1>
            <p className="text-xl text-white/90 max-w-3xl">
              Pellet, legna, mangimi, terricci e concimi portati direttamente a casa, in azienda o in stalla.
              Dal 1983 serviamo il lago Trasimeno e la zona di Perugia.
            </p>
          </div>
        </div>
      </section>

      {/* Zone servite */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Dove consegniamo</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Copriamo un raggio di circa 30 chilometri dal negozio di Viale Umbria, a Magione.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {ZONE.map((zona) => (
              <div key={zona.area} className="bg-card rounded-2xl p-8 border border-border card-hover">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-muted rounded-full mb-4">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-heading font-semibold mb-4">{zona.area}</h3>
                <ul className="space-y-2">
                  {zona.comuni.map((comune) => (
                    <li key={comune} className="flex items-start">
                      <span className="text-primary mr-2 flex-shrink-0">✓</span>
                      <span className="text-sm">{comune}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="text-center text-muted-foreground mt-10 max-w-2xl mx-auto">
            Consegniamo anche nelle frazioni e nei comuni più piccoli compresi nello stesso raggio.
            Se non trovi il tuo paese in elenco chiamaci lo stesso: fuori zona valutiamo di volta in volta,
            soprattutto per gli ordini più grandi.
          </p>
        </div>
      </section>

      {/* Cosa consegniamo */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Cosa portiamo a domicilio</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Tutto quello che trovi in negozio, con un occhio di riguardo per il pesante e l'ingombrante
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {COSA_CONSEGNAMO.map((voce) => (
              <Link
                key={voce.titolo}
                to={voce.link}
                className="bg-card rounded-2xl p-8 border border-border card-hover block"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-muted rounded-full mb-4">
                  <voce.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-xl font-heading font-semibold mb-3">{voce.titolo}</h3>
                <p className="text-muted-foreground">{voce.testo}</p>
                <span className="text-primary text-sm font-medium mt-4 inline-block">Vedi i prodotti →</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Come si prenota */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">Come si prenota</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Non serve un ordine online: si fa tutto con una telefonata o un messaggio
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-card p-8 rounded-2xl border border-border text-center card-hover">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                <Phone className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-3">1. Ci contatti</h3>
              <p className="text-muted-foreground">
                Telefono, WhatsApp o email: ci dici cosa ti serve e dove abiti.
              </p>
            </div>

            <div className="bg-card p-8 rounded-2xl border border-border text-center card-hover">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                <MessageCircle className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-3">2. Concordiamo</h3>
              <p className="text-muted-foreground">
                Verifichiamo la disponibilità, ti diciamo il prezzo e fissiamo insieme il giorno della consegna.
              </p>
            </div>

            <div className="bg-card p-8 rounded-2xl border border-border text-center card-hover">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
                <Truck className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-heading font-semibold mb-3">3. Consegniamo</h3>
              <p className="text-muted-foreground">
                Arriviamo con il nostro mezzo e scarichiamo sul posto, anche i bancali interi.
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <a
              href="tel:+393466257639"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
            >
              <Phone className="w-4 h-4" />
              346 625 7639
            </a>
            <a
              href="https://wa.me/393466257639"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              Scrivici su WhatsApp
            </a>
            <a
              href="mailto:mangimirubini@gmail.com"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <Mail className="w-4 h-4" />
              Scrivici una email
            </a>
          </div>
        </div>
      </section>

      {/* Professionisti */}
      <section className="py-16 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-heading font-bold mb-4">Consegne per aziende e allevamenti</h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Se hai un allevamento, un vivaio o un'attività agricola gestiamo forniture ricorrenti e grandi
            volumi, con prezzi dedicati agli acquisti importanti.
          </p>
          <Link
            to="/professionisti"
            className="inline-block px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
          >
            Servizi per professionisti
          </Link>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default Consegne;
